import { Select } from '@customerio/pluma-components/react';
import { entriesToOneOfValue, oneOfValueToEntries, splitOneOfInput } from '../../one-of-value';
import { toValueOptions, type ConditionControlSize, type OperatorValuePatch, type ValueOption } from './types';
import { MaskedValueOption } from './value-combobox';

export type OneOfValueSelectProps = {
  disabled?: boolean;
  /** The stored list, a marshalled JSON array of strings, or null for none. */
  value: string | null;
  valueOptions: ValueOption[];
  isLoadingValueOptions?: boolean;
  error?: string;
  onChange: (patch: OperatorValuePatch) => void;
  /** Marks the editor visited, so its error can surface. */
  onTouched: () => void;
  size?: ConditionControlSize;
};

/**
 * The list a `one_of` condition matches against: pick suggested values, or
 * type new ones, which a comma splits into separate entries.
 *
 * Every change writes the whole list back as its marshalled value, or null
 * once the last entry is removed.
 */
export function OneOfValueSelect({
  disabled,
  value,
  valueOptions,
  isLoadingValueOptions,
  error,
  onChange,
  onTouched,
  size = 'sm',
}: OneOfValueSelectProps) {
  const entries = oneOfValueToEntries(value);

  // A chosen entry needs an option of its own to render as a chip, and a typed
  // one is not among the suggestions.
  const suggested = new Set(valueOptions.map((option) => option.value));
  const options = [...toValueOptions(entries.filter((entry) => !suggested.has(entry))), ...valueOptions];

  const setEntries = (next: readonly string[]) => onChange({ value: entriesToOneOfValue(next) });

  const createEntries = (input: string) => {
    const added = [...new Set(splitOneOfInput(input))].filter((entry) => !entries.includes(entry));
    setEntries([...entries, ...added]);
  };

  return (
    <Select
      data-testid="dryad-filter-one-of-value-select"
      ariaLabel="Values"
      className="fs-mask"
      size={size}
      placeholder="Select a value"
      value={entries}
      options={options}
      optionComponent={MaskedValueOption}
      isLoading={isLoadingValueOptions}
      error={error}
      isMulti
      canWrap
      isSearchable
      withCreate
      onCreate={createEntries}
      onChange={(values: readonly string[] | null) => setEntries(values ?? [])}
      // The select has no blur of its own; closing the dropdown is the visit.
      onOpenChange={(isOpen) => {
        if (!isOpen) {
          onTouched();
        }
      }}
      isDisabled={disabled}
    />
  );
}
